/**
 * Three zones, and the two things that cross between them.
 *
 * What crosses: a websocket frame from the browser to the control plane, and an
 * mTLS link from the control plane to the host. What does **not** cross: the
 * credential. It stays inside the host's box, and the box is drawn closed.
 *
 * The second claim is the log line. A line leaves the host carrying a token,
 * and by the time it is on the other side of the boundary the secret is gone —
 * because redaction lives in the slog handler rather than at the call site, so
 * there is no logging path that can be written which forgets it.
 *
 * The browser is deliberately drawn with no arrow to the host at all. Every
 * byte is proxied; the console never opens a socket to a machine.
 */
import { PathDraw, Token } from '../../kit'
import { useT } from '../../i18n/context'
import './TrustBoundary.css'

/** Two hops, laid out once so the stroke and the token cannot disagree. */
const HOP_A = 'M 214 108 H 340'
const HOP_B = 'M 560 108 H 686'
/** The log line, leaving the host and crossing back over the boundary. */
const LOG = 'M 760 176 V 232 H 450'

export function TrustBoundary() {
  const t = useT()

  return (
    <div className="kl-trust">
      <svg viewBox="0 0 900 300" className="kl-trust__svg" preserveAspectRatio="xMidYMid meet">
        <defs>
          <marker id="kl-trust-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
            <path d="M0 0 L8 4 L0 8 z" className="kl-trust__arrowhead" />
          </marker>
        </defs>

        {[
          { x: 40, w: 174, label: 'browser' },
          { x: 340, w: 220, label: 'kimene-server' },
          { x: 686, w: 174, label: 'srv-ist-1' },
        ].map((zone) => (
          <g key={zone.label}>
            <rect
              x={zone.x}
              y={44}
              width={zone.w}
              height={132}
              rx={6}
              className={`kl-trust__zone ${zone.label === 'srv-ist-1' ? 'kl-trust__zone--host' : ''}`}
            />
            <text x={zone.x + zone.w / 2} y={70} className="kl-trust__zoneLabel k-mono">
              {zone.label}
            </text>
          </g>
        ))}

        {/* The credential. Inside the host, and the only thing with a keyhole. */}
        <g transform="translate(773, 128)">
          <rect x={-64} y={-16} width={128} height={32} rx={4} className="kl-trust__cred" />
          <text y={5} className="kl-trust__credLabel k-mono">
            CLAUDE_CONFIG_DIR
          </text>
        </g>
        <text x={773} y={166} className="kl-trust__pin">
          {t('security.point.hostLocal')}
        </text>

        <PathDraw d={HOP_A} className="kl-trust__link" markerEnd="url(#kl-trust-arrow)" />
        <PathDraw d={HOP_B} className="kl-trust__link" markerEnd="url(#kl-trust-arrow)" delay={0.2} />
        <text x={277} y={98} className="kl-trust__hop k-mono">
          wss
        </text>
        <text x={623} y={98} className="kl-trust__hop k-mono">
          mTLS
        </text>

        <Token d={HOP_A} duration={1.1} />
        <Token d={HOP_B} duration={1.1} delay={0.3} />

        {/* The log line, and what happens to it at the boundary. */}
        <PathDraw d={LOG} className="kl-trust__log" delay={0.5} />
        <text x={782} y={196} className="kl-trust__logText k-mono kl-trust__logText--raw">
          token=sk-ant-…
        </text>
        <text x={438} y={252} className="kl-trust__logText k-mono">
          token=[REDACTED]
        </text>
      </svg>

      <ul className="kl-trust__points">
        {(['security.point.mtls', 'security.point.redaction', 'security.point.proxied'] as const).map(
          (key) => (
            <li key={key}>{t(key)}</li>
          ),
        )}
      </ul>
    </div>
  )
}
